import { motion } from "framer-motion";

export function PageHeader({ title, subtitle, action, eyebrow }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8"
      data-testid="page-header"
    >
      <div>
        {eyebrow && (
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-mustard-hover mb-2">{eyebrow}</div>
        )}
        <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-ink">
          {title}
        </h1>
        {subtitle && (
          <p className="text-ink-soft mt-2 text-sm md:text-base max-w-2xl">{subtitle}</p>
        )}
      </div>
      {action && <div className="flex gap-2">{action}</div>}
    </motion.div>
  );
}

export function StatCard({ label, value, hint, accent = "terracotta", icon: Icon, testid }) {
  const map = {
    terracotta: "bg-terracotta-soft text-terracotta",
    mustard: "bg-mustard-soft text-mustard-hover",
    sage: "bg-sage-soft text-sage-deep",
    lavender: "bg-lavender-soft text-lavender-deep",
    ink: "bg-paper-alt text-ink",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      whileHover={{ y: -3 }}
      className="bg-white rounded-3xl border border-border p-6 shadow-soft hover:shadow-hoverlift transition-all"
      data-testid={testid || `stat-${label?.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm text-ink-soft font-medium">{label}</div>
        {Icon && (
          <span className={`w-9 h-9 rounded-2xl flex items-center justify-center ${map[accent]}`}>
            <Icon size={18} weight="duotone" />
          </span>
        )}
      </div>
      <div className="mt-3 font-heading font-bold text-3xl tracking-tight text-ink">{value}</div>
      {hint && <div className="mt-1 text-xs text-ink-soft">{hint}</div>}
    </motion.div>
  );
}

export function Panel({ title, action, children, className = "", testid }) {
  return (
    <section
      className={`bg-white rounded-3xl border border-border p-6 shadow-soft ${className}`}
      data-testid={testid}
    >
      {(title || action) && (
        <div className="flex items-center justify-between mb-5">
          {title && <h3 className="font-heading font-semibold text-lg text-ink">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Pill({ tone = "ink", children, className = "" }) {
  const tones = {
    ink: "bg-paper-alt text-ink",
    terracotta: "bg-terracotta-soft text-terracotta",
    mustard: "bg-mustard-soft text-mustard-hover",
    sage: "bg-sage-soft text-sage-deep",
    lavender: "bg-lavender-soft text-lavender-deep",
    danger: "bg-red-50 text-red-700",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${tones[tone] || tones.ink} ${className}`}>
      {children}
    </span>
  );
}

export function EmptyState({ title = "Nothing here yet", hint, action }) {
  return (
    <div className="text-center py-12" data-testid="empty-state">
      <div className="text-5xl mb-3">🌱</div>
      <div className="font-heading font-semibold text-lg">{title}</div>
      {hint && <div className="text-ink-soft text-sm mt-1 max-w-md mx-auto">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Avatar({ name, size = 40, color }) {
  const initials = name?.split(" ").map((w) => w[0]).slice(0, 2).join("") || "·";
  const palette = ["bg-terracotta-soft text-terracotta", "bg-mustard-soft text-mustard-hover", "bg-sage-soft text-sage-deep", "bg-lavender-soft text-lavender-deep"];
  const idx = (name?.charCodeAt(0) || 0) % palette.length;
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold shrink-0 ${color || palette[idx]}`}
      style={{ width: size, height: size, fontSize: Math.max(11, size / 3) }}
    >
      {initials}
    </span>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Phase 5 — Interactivity polish primitives
// ─────────────────────────────────────────────────────────────────────


/**
 * Skeleton — shimmer block. Use to fill space while data is loading
 * so the UI doesn't visibly reflow when the response arrives.
 *
 *    <Skeleton height={28} width="60%" />
 *    <Skeleton variant="card" />
 *    <Skeleton.Row count={5} />
 */
export function Skeleton({ height = 16, width = "100%", variant, className = "" }) {
  const cls = variant === "card"
    ? "h-32 w-full rounded-2xl"
    : variant === "circle"
    ? "rounded-full"
    : "rounded-lg";
  return (
    <div
      className={`bg-paper-alt animate-pulse ${cls} ${className}`}
      style={{ height: variant === "card" ? undefined : height,
                width: variant === "circle" ? height : width }}
      aria-hidden="true"
    />
  );
}

Skeleton.Row = function SkeletonRow({ count = 3 }) {
  return (
    <div className="space-y-2.5" data-testid="skeleton-rows">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton variant="circle" height={36} />
          <div className="flex-1 space-y-1.5">
            <Skeleton height={14} width="55%" />
            <Skeleton height={10} width="30%" />
          </div>
        </div>
      ))}
    </div>
  );
};


/**
 * StatusBadge — colour-coded status pill with a leading dot.
 *
 * Standard statuses for the platform:
 *   active   → green  (live / OK)
 *   pending  → amber  (waiting on user / setup link sent)
 *   inactive → grey   (deactivated)
 *   blocked  → red    (suspended / failed)
 *   info     → blue   (neutral marker)
 */
export function StatusBadge({ status = "info", label, testid }) {
  const map = {
    active:   { dot: "bg-sage-deep",      bg: "bg-sage-soft",      text: "text-sage-deep" },
    pending:  { dot: "bg-mustard-hover",  bg: "bg-mustard-soft",   text: "text-mustard-hover" },
    inactive: { dot: "bg-ink-soft",       bg: "bg-paper-alt",      text: "text-ink-soft" },
    blocked:  { dot: "bg-red-500",        bg: "bg-red-50",         text: "text-red-700" },
    info:     { dot: "bg-lavender-deep",  bg: "bg-lavender-soft",  text: "text-lavender-deep" },
    hot:      { dot: "bg-rose-500",       bg: "bg-rose-50",        text: "text-rose-700" },
    warm:     { dot: "bg-amber-500",      bg: "bg-amber-50",       text: "text-amber-800" },
    cold:     { dot: "bg-sky-500",        bg: "bg-sky-50",         text: "text-sky-800" },
  };
  const tone = map[status] || map.info;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${tone.bg} ${tone.text}`}
      data-testid={testid}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${tone.dot}`} />
      {label || status}
    </span>
  );
}


/**
 * Tooltip — CSS-only tooltip for icon buttons. Pure HTML title attribute
 * is too slow + ugly; this gives us a styled hover affordance with zero
 * JS. Wrap any element:
 *
 *    <Tooltip text="Edit row"><button>...</button></Tooltip>
 */
export function Tooltip({ text, children, side = "top", className = "" }) {
  const sideCls = {
    top:    "bottom-full left-1/2 -translate-x-1/2 mb-1",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-1",
    left:   "right-full top-1/2 -translate-y-1/2 mr-1",
    right:  "left-full top-1/2 -translate-y-1/2 ml-1",
  }[side];
  return (
    <span className={`relative inline-flex group ${className}`}>
      {children}
      <span className={`pointer-events-none absolute ${sideCls} z-50
                         opacity-0 group-hover:opacity-100 transition-opacity duration-150
                         whitespace-nowrap rounded-md bg-ink text-white text-[11px]
                         font-medium px-2 py-1 shadow-floating`}>
        {text}
      </span>
    </span>
  );
}
